import React from "react";
import type { ScorecardData, ScorecardField, ScorecardValue } from "@/types/scorecard";
import { ScorecardFieldInput } from "./ScorecardFieldInput";
import { isFieldCompleted } from "./scorecardUtils";

interface ScorecardCategorySectionProps {
	title: string;
	description?: string | null;
	color?: string | null;
	fields: ScorecardField[];
	values: ScorecardData;
	onChange: (fieldId: string, value: ScorecardValue) => void;
	showRequiredHints: boolean;
}

/** One scorecard section: category header, completion count, and its fields. */
export const ScorecardCategorySection: React.FC<ScorecardCategorySectionProps> = ({
	title,
	description,
	color,
	fields,
	values,
	onChange,
	showRequiredHints,
}) => {
	const completed = fields.filter((field) => isFieldCompleted(field, values[field.id])).length;
	const percentage = fields.length === 0 ? 0 : Math.round((completed / fields.length) * 100);

	return (
		<section className="rounded-sm border border-border bg-card">
			<header className="border-b border-border px-4 py-3">
				<div className="flex items-center justify-between gap-2">
					<div className="flex min-w-0 items-center gap-2">
						<span
							className="h-2.5 w-2.5 shrink-0 rounded-full"
							style={{ backgroundColor: color || "hsl(var(--muted-foreground))" }}
							aria-hidden="true"
						/>
						<h3 className="truncate text-sm font-medium text-foreground">{title}</h3>
					</div>
					<span className="shrink-0 text-xs text-muted-foreground">
						{completed} / {fields.length}
					</span>
				</div>
				{description && <p className="mt-1 text-xs text-muted-foreground">{description}</p>}
				<div className="mt-2 h-1 overflow-hidden rounded-full bg-muted" aria-hidden="true">
					<div
						className="h-full rounded-full transition-all"
						style={{
							width: `${percentage}%`,
							backgroundColor: color || "hsl(var(--primary))",
						}}
					/>
				</div>
			</header>
			<div className="space-y-5 p-4">
				{fields.map((field) => (
					<ScorecardFieldInput
						key={field.id}
						field={field}
						value={values[field.id]}
						onChange={onChange}
						showRequiredHint={showRequiredHints}
					/>
				))}
			</div>
		</section>
	);
};
